"use strict";

// ---------------------------------------------------
// Load Super Class

var SoaCollection = require('bunsen/collections/SoaCollection');

// ---------------------------------------------------
// Module Definition

/**
 * @class ShippingCosts
 * @extends SoaCollection
 */
var ShippingCosts = module.exports = SoaCollection.extend();

// ---------------------------------------------------
// Dependencies

var _ = require('underscore');
var ShippingCost = require('bunsen/models/logistics/ShippingCost');
var endpoints = require('dibs-endpoints');
var dataProvider = require('bunsen/helpers/dataProvider');
var getDeferredMethods = require('bunsen/helpers/getDeferredMethods');
var User = require('bunsen/models/identity/User');
var globalCache = require('bunsen/helpers/globalCache');

var deferred = getDeferredMethods().Deferred;

var extendFetchData = function (fetchOptions, data) {
    fetchOptions = fetchOptions ? _.clone(fetchOptions) : {};
    fetchOptions.data = _.extend({}, fetchOptions.data, data);
    return fetchOptions;
};

// ---------------------------------------------------
// Define Module Instance Properties

_.extend(ShippingCosts.prototype, /** @lends ShippingCosts.prototype */{

    url: endpoints.shippingCosts,

    model: ShippingCost,

    /**
     * @returns {ShippingCost}
     */
    findUnknownCost: function () {
        return this.findByCostType(ShippingCost.COST_TYPE_UNKNOWN);
    },

    /**
     * @returns {ShippingCost}
     */
    findTransitCost: function () {
        return this.findByCostType(ShippingCost.COST_TYPE_TRANSIT);
    },

    /**
     * @param {string} costType
     * @returns {ShippingCost}
     */
    findByCostType: function (costType) {
        return this.find(function (shippingCost) {
            return shippingCost.isCostType(costType);
        });
    },

    /**
     * @param {string} costType
     * @returns {FinancialAmount}
     */
    getSubTotalAmountForType: function (costType) {
        var cost = this.findByCostType(costType);
        return cost ? cost.getSubTotalAmount() : null;
    },

    /**
     * @param {string} costType
     * @returns {number}
     */
    getSubTotalNumForType: function (costType) {
        var cost = this.findByCostType(costType);
        return cost ? cost.getSubTotalNum() : null;
    },

    /**
     * @param {string} costType
     * @returns {Party}
     */
    getBillToForType: function (costType) {
        var cost = this.findByCostType(costType);
        return cost ? cost.getBillTo() : null;
    },

    /**
     * @param {string} costType
     * @returns {string}
     */
    getBillToRoleForType: function (costType) {
        var cost = this.findByCostType(costType);
        return cost ? cost.getBillToRole() : null;
    },

    /**
     * Returns the reference shipping cost. Returns the unknown
     * shipping cost if present, otherwise returns the "transit"
     * shipping cost. Finally, returns null if neither type is
     * present in the collection.
     * @returns {ShippingCost}
     */
    findDefaultCost : function () {
        return this.find(function (cost) {
            return cost.isDefaultCost();
        });
    },

    /**
     * Removes a shipping cost that matches the provided cost
     * type, otherwise does nothing.
     */
    removeByCostType: function (costType) {
        var cost = this.findByCostType(costType);
        if (cost) {
            this.remove(cost);
        }
        return this;
    },

    /**
     * @param {string} costType
     * @returns {string}
     */
    findBillToCarrierNameForType: function (costType) {
        var cost = this.findByCostType(costType);
        return cost ? cost.findBillToCarrierName() : null;
    },

    /**
     * @param {string} costType
     * @returns {string}
     */
    findBillToCarrierCodeForType: function (costType) {
        var cost = this.findByCostType(costType);
        return cost ? cost.findBillToCarrierCode() : null;
    },

    /**
     * Returns true if the only cost in the collection
     * is an UNKNOWN cost.
     * @returns {boolean}
     */
    hasOnlyUnknownCost: function () {
        return this.length === 1 && !!this.findUnknownCost();
    },

    /**
     * @param {string} costType
     * @param {number} subTotal
     * @param {string} [currency]
     * @returns {ShippingCosts}
     */
    setSubTotalForType: function (costType, subTotal, currency) {
        var cost = this.findByCostType(costType);
        if (!currency) {
            currency = this.getDefaultCurrency();
        }
        if (!cost) {
            this.addWithTypeAndSubTotal(costType, subTotal, currency);
            return this;
        }
        cost.setSubTotalAndCurr(subTotal, currency);
        return this;
    },

    /**
     * @param {string} costType
     * @param {string} [role]
     * @param {number} [id]
     * @returns {ShippingCosts}
     */
    setBillToForType : function (costType, role, id) {
        var cost = this.findByCostType(costType);
        var billTo = { role: role, id: id };
        if (!cost) {
            this._addQuoteWithConfig({
                costType: costType,
                billTo: billTo
            });
        } else {
            cost.setBillTo(billTo);
        }
        return this;
    },

    /**
     * @param {string} [costType]
     * @param {number} [subTotal]
     * @param {string} [currency]
     * @returns {ShippingCosts}
     */
    addWithTypeAndSubTotal: function (costType, subTotal, currency) {
        if (!currency) {
            currency = this.getDefaultCurrency();
        }
        this._addQuoteWithConfig({
            subTotal: subTotal,
            costType: costType,
            currency: currency
        });
        return this;
    },

    /**
     * @param {string} defaultCurrency
     * @returns {ShippingCosts}
     */
    setDefaultCurrency: function (defaultCurrency) {
        this._defaultCurrency = defaultCurrency;
        return this;
    },

    /**
     * @returns {string}
     */
    getDefaultCurrency: function () {
        var defCurrency = this._defaultCurrency;
        var first;
        if (defCurrency) {
            return defCurrency;
        }
        first = this.first();
        return first ? first.getCurrency() : null;
    },

    /**
     * @param {Party} defaultAuthor
     * @returns {ShippingCosts}
     */
    setDefaultAuthor: function (defaultAuthor) {
        this._defaultAuthor = defaultAuthor;
        return this;
    },

    /**
     * @returns {object}
     */
    getDefaultAuthor: function () {
        return this._defaultAuthor;
    },

    _addQuoteWithConfig: function (config) {
        config.currency = config.currency || this.getDefaultCurrency();
        var defaultAuthor = this.getDefaultAuthor();
        var shippingCost = ShippingCost.createWithConfig(config);
        if (defaultAuthor) {
            shippingCost.setAuthor(defaultAuthor);
        }
        this.add(shippingCost);
        return shippingCost;
    }

});

// ---------------------------------------------------
// Define Module Static Properties

_.extend(ShippingCosts, /** @lends ShippingCosts */{

    _shippingCostsInstances: {},

    VIEW_NAME_SELLER_DEFAULTS: 'SELLER_DEFAULTS',

    VIEW_NAME_ADMIN_DEFAULTS: 'ADMIN_DEFAULTS',

    /**
     * @static
     * @private
     * @param {string} userType
     * @returns {symbol}
     */
    _getCostsCacheKeyForUserType: (function () {
        var cacheKeys = {};
        return function (userType) {
            if (!cacheKeys[userType]) {
                cacheKeys[userType] = Symbol('fetching_costs_' + userType);
            }
            return cacheKeys[userType];
        };
    }()),

    /**
     * The default costType is 'unknown' which is used
     * by dealers who only enter one cost. This static
     * method can be used to quickly create a ShippingCosts
     * collection with one unknown shipping cost.
     * @param {number} subTotal
     * @param {string} currency
     * @returns {ShippingCosts}
     */
    createWithUnknownCost: function (subTotal, currency) {
        var unknownCost = ShippingCost.createUnknownCostWithSubTotal(subTotal, currency);
        return new ShippingCosts([unknownCost]);
    },

    /**
     * @public
     * @static
     * @returns {ShippingCosts}
     */
    sellerDefaultCostsInstance: function () {
        return ShippingCosts.defaultUserTypeShippingCosts(User.TYPE_SELLER);
    },

    /**
     * @public
     * @static
     * @returns {ShippingCosts}
     */
    adminDefaultCostsInstance: function () {
        return ShippingCosts.defaultUserTypeShippingCosts(User.TYPE_INTERNAL);
    },

    /**
     * @public
     * @static
     * @returns {ShippingCosts}
     */
    defaultUserTypeShippingCosts: function (userType) {
        var instance = ShippingCosts._shippingCostsInstances[userType];
        if (!instance) {
            instance = ShippingCosts._shippingCostsInstances[userType] = new ShippingCosts();
        }
        return instance;
    },

    /**
     * @param {object} fetchOptions Standard Backbone Model fetch options
     * @returns {Deferred.promise}
     */
    fetchSellerDefaultCostsInstance: function (fetchOptions) {
        return ShippingCosts.fetchDefaultShippingCostsInstance(User.TYPE_SELLER, fetchOptions);
    },

    /**
     * @param {object} fetchOptions Standard Backbone Model fetch options
     * @returns {Deferred.promise}
     */
    fetchAdminDefaultCostsInstance: function (fetchOptions) {
        return ShippingCosts.fetchDefaultShippingCostsInstance(User.TYPE_INTERNAL, fetchOptions);
    },

    /**
     * @public
     * @static
     * @param {object} userType The user type to fetch defaults for
     * @param {object} [fetchOptions] Standard Backbone.fetch options
     * @returns {Deferred.promise}
     */
    fetchDefaultShippingCostsInstance: function (userType, fetchOptions) {
        var viewName = userType === User.TYPE_INTERNAL ? ShippingCosts.VIEW_NAME_ADMIN_DEFAULTS : ShippingCosts.VIEW_NAME_SELLER_DEFAULTS;
        var cacheKey = ShippingCosts._getCostsCacheKeyForUserType(userType);
        var costsProm = globalCache.get(cacheKey);

        fetchOptions = extendFetchData(fetchOptions, { view: viewName });
        if (!costsProm) {
            costsProm = ShippingCosts.defaultUserTypeShippingCosts(userType).fetch(fetchOptions);
            globalCache.set(cacheKey, costsProm);
            globalCache.get(cacheKey);
            costsProm.fail(function () {
                globalCache.del(cacheKey);
            });
        }
        return costsProm;
    },

    /**
     * For the standard ShippingCosts singleton, if there is data
     * already set on the dataProvider, resets the collection to
     * use that data. Otherwise, fetches the data from the services.
     * @static
     * @public
     * @param {string} userType
     * @param {string} dataProviderKey
     * @param {object} [fetchOptions] Standard Backbone.fetch options
     * @returns {Deferred.promise}
     */
    requestDefaultShippingCostsInstanceUseDP: function (userType, dataProviderKey, fetchOptions) {
        var data = dataProvider.get(dataProviderKey || 'default_shipping_costs');
        var instance;
        if (data) {
            instance = ShippingCosts.defaultUserTypeShippingCosts(userType);
            if (data.length !== instance.length) {
                instance.reset(data);
            }
            return deferred().resolve(data).promise();
        }
        return ShippingCosts.fetchDefaultShippingCostsInstance(fetchOptions);
    },

    /**
     * @param {string} [dataProviderKey]
     * @param {object} [fetchOptions] Standard Backbone.fetch options
     * @returns {Deferred.promise}
     */
    requestSellerDefaultCostsUseDP: function (dataProviderKey, fetchOptions) {
        return this.requestDefaultShippingCostsInstanceUseDP(User.TYPE_SELLER, dataProviderKey, fetchOptions);
    },

    /**
     * @param {string} [dataProviderKey]
     * @param {object} [fetchOptions] Standard Backbone.fetch options
     * @returns {Deferred.promise}
     */
    requestAdminDefaultCostsUseDP: function (dataProviderKey, fetchOptions) {
        return this.requestDefaultShippingCostsInstanceUseDP(User.TYPE_INTERNAL, dataProviderKey, fetchOptions);
    }

});



// WEBPACK FOOTER //
// ./~/bunsen/collections/logistics/ShippingCosts.es.js